import { Helmet } from 'react-helmet-async';
import { Vehicle } from '@/types';
import { getVehicleSeoTitle, getVehicleSeoDescription } from '@/utils/vehicleSlug';
import { generateVehicleProductSchema } from '@/utils/seoHelpers';

interface VehicleSEOProps {
  vehicle: Vehicle;
}

export const VehicleSEO = ({ vehicle }: VehicleSEOProps) => {
  const title = getVehicleSeoTitle(vehicle);
  const description = getVehicleSeoDescription(vehicle);
  const canonicalUrl = `https://trucks52-sarl.lovable.app${window.location.pathname}`;
  const imageUrl = vehicle.images?.[0] || 'https://trucks52-sarl.lovable.app/lovable-uploads/6d0b544e-d8d8-4f71-9ceb-c4652b9364aa.png';

  // Générer le schéma Product
  const productSchema = generateVehicleProductSchema(vehicle);

  // Mots-clés SEO
  const keywords = [
    vehicle.brand,
    'camion occasion',
    'véhicule industriel',
    vehicle.category,
    vehicle.euroStandard,
    'Haute-Marne',
    'Val-de-Meuse',
    'Trucks 52',
    vehicle.year?.toString(),
    vehicle.transmission,
    vehicle.fuelType,
  ].filter(Boolean).join(', ');

  return (
    <Helmet>
      {/* Balises meta de base */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="product" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Trucks 52 SARL" />
      <meta property="og:locale" content="fr_FR" />

      {/* Product specific OG tags */}
      {(vehicle.priceTTC || vehicle.price) && (
        <>
          <meta property="product:price:amount" content={(vehicle.priceTTC || vehicle.price).toString()} />
          <meta property="product:price:currency" content="EUR" />
        </>
      )}
      {vehicle.brand && <meta property="product:brand" content={vehicle.brand} />}
      {vehicle.category && <meta property="product:category" content={vehicle.category} />}
      <meta property="product:availability" content={vehicle.isAvailable ? "in stock" : "out of stock"} />
      <meta property="product:condition" content="used" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Schema.org Product JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(productSchema)}
      </script>

      {/* Balises géo-localisées */}
      <meta name="geo.region" content="FR-52" />
      <meta name="geo.placename" content="Val-de-Meuse" />
      <meta name="geo.position" content="48.0942;5.4964" />
      <meta name="ICBM" content="48.0942, 5.4964" />
    </Helmet>
  );
};
